import { useEffect, useState } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import api from '../../utils/api'
import { formatInstallerDisplayName } from '../../utils/installerName'
import PackageSearchSelect from './PackageSearchSelect'

const ACTION_OPTIONS = [
  { value: '', label: '全部动作' },
  { value: 'open', label: '打开' },
  { value: 'close', label: '关闭' }
]

export default function InstallOpenRecordsTab() {
  const [loading, setLoading] = useState(false)
  const [packagesLoading, setPackagesLoading] = useState(true)
  const [packages, setPackages] = useState([])
  const [installerNames, setInstallerNames] = useState([])
  const [action, setAction] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [records, setRecords] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 })
  const [jumpPage, setJumpPage] = useState('')

  const fetchPackages = async () => {
    setPackagesLoading(true)
    try {
      const res = await api.get('/admin/download-package-names')
      if (res.data.success) {
        const data = res.data.data || res.data || {}
        setPackages(data.list || [])
      }
    } catch (error) {
      console.error('获取推广安装包列表失败:', error)
    } finally {
      setPackagesLoading(false)
    }
  }

  const fetchRecords = async (page = 1, filters = {}) => {
    const nextInstallerNames = Array.isArray(filters.installerNames) ? filters.installerNames : installerNames
    const nextStartDate = typeof filters.startDate === 'string' ? filters.startDate : startDate
    const nextEndDate = typeof filters.endDate === 'string' ? filters.endDate : endDate
    const nextAction = typeof filters.action === 'string' ? filters.action : action

    setLoading(true)
    try {
      const res = await api.get('/admin/install-open-records', {
        params: {
          page,
          limit: pagination.limit,
          installerNames: nextInstallerNames.length > 0 ? nextInstallerNames : undefined,
          action: nextAction || undefined,
          startDate: nextStartDate || undefined,
          endDate: nextEndDate || undefined
        }
      })
      if (res.data.success) {
        const data = res.data.data || res.data || {}
        const pager = data.pagination || {}
        setRecords(data.list || [])
        setPagination({
          page: pager.page || 1,
          limit: pager.limit || pagination.limit,
          total: pager.total || 0,
          totalPages: pager.totalPages || 1
        })
      }
    } catch (error) {
      console.error('获取安装打开记录失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPackages()
  }, [])

  useEffect(() => {
    if (packagesLoading) {
      return
    }
    fetchRecords(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packagesLoading])

  const handleSearch = (e) => {
    e.preventDefault()
    fetchRecords(1)
  }

  const handleReset = () => {
    setInstallerNames([])
    setAction('')
    setStartDate('')
    setEndDate('')
    setJumpPage('')
    fetchRecords(1, {
      installerNames: [],
      action: '',
      startDate: '',
      endDate: ''
    })
  }

  const handleJump = () => {
    const page = Number(jumpPage)
    if (!jumpPage.trim() || isNaN(page)) {
      setJumpPage('')
      return
    }
    if (page < 1 || page > pagination.totalPages) {
      alert(`请输入 1 ~ ${pagination.totalPages} 之间的页码`)
      return
    }
    fetchRecords(page)
    setJumpPage('')
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">安装打开记录</h2>
        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-2">
          <PackageSearchSelect
            value={installerNames}
            onChange={setInstallerNames}
            options={packages}
            loading={packagesLoading}
            multiple
            placeholder="全部安装包"
          />
          <select
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            {ACTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button type="button" onClick={handleReset} className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200">
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchRecords(pagination.page)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
        </form>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">标签</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">安装包名</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">动作</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">来源</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">IP</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">UA</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {records.length > 0 ? (
                  records.map((item, index) => {
                    const displayInstallerName = formatInstallerDisplayName(item.installer_name)

                    return (
                      <tr key={`${item.created_at}-${item.ip}-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{item.installer_label || item.installer_name || '-'}</td>
                        <td className="max-w-[260px] truncate px-6 py-4 text-sm text-gray-900" title={displayInstallerName || undefined}>
                          {displayInstallerName || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {item.action === 'open' ? (
                            <span className="inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              打开
                            </span>
                          ) : item.action === 'close' ? (
                            <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-900/50 dark:text-gray-400">
                              关闭
                            </span>
                          ) : (
                            <span className="text-gray-900">{item.action || '-'}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.source || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.ip || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{item.ua || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.created_at || '-'}</td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无数据
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between bg-gray-50 px-6 py-4">
            <div className="text-sm text-gray-600">
              共 {pagination.total} 条，第 {pagination.page}/{pagination.totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                placeholder="页码"
                min={1}
                max={pagination.totalPages}
                className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleJump}
                className="rounded-lg border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                跳转
              </button>
              <button
                disabled={pagination.page <= 1}
                onClick={() => fetchRecords(pagination.page - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                上一页
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchRecords(pagination.page + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
